import PropTypes from 'prop-types';
import React from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Divider,
  Stack,
  Typography,
  Button,
  TextField,
  Box,
  FormControl,
  FormLabel,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Autocomplete,
  FormGroup,
  Checkbox,
  useMediaQuery
} from '@mui/material';
import { toast } from 'react-hot-toast';
import _ from 'lodash';
import arrowBackFilled from '@iconify/icons-eva/arrow-back-fill';
import plusCircleFill from '@iconify/icons-eva/plus-circle-fill';
import questionMarkCircleFill from '@iconify/icons-eva/question-mark-circle-fill';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';
import arrowCircleUpOutline from '@iconify/icons-eva/arrow-circle-up-outline';
import arrowCircleDownOutline from '@iconify/icons-eva/arrow-circle-down-outline';
import edit2Outline from '@iconify/icons-eva/edit-2-outline';
import personOutline from '@iconify/icons-eva/person-outline';
import menu2Outline from '@iconify/icons-eva/menu-2-outline';
import eyeFill from '@iconify/icons-eva/eye-fill';
import shareFill from '@iconify/icons-eva/share-fill';
import { Icon } from '@iconify/react';
import { v4 as uuidv4 } from 'uuid';
import { LoadingButton } from '@mui/lab';
import moment from 'moment-timezone';
//
import Page from '../components/Page';
import * as uatServices from '../services/uat';
import * as userService from '../services/user';
import * as instansiService from '../services/instansi';
import * as myLayananService from '../services/mylayanan';
import findIndex from '../utils/indexFinder';
import useWidth from '../hooks/useWidth';
import { getBaseURL } from '../utils/envGetter';
import RekomendasiCTA from '../components/RekomendasiCTA';

const UATCreator = ({ user }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const role = user?.attributes?.role;

  const baseStyle = {
    padding: 1,
    cursor: 'pointer'
  };

  const styles = {
    selectedMenu: {
      color: theme.palette.primary.main,
      ...baseStyle
    },
    notSelectedMenu: {
      color: '#424242',
      ...baseStyle
    }
  };

  const [toggleRefresh, setToggleRefresh] = React.useState(false);
  const [uat, setUat] = React.useState({});
  const [selectedMenu, setSelectedMenu] = React.useState('formbuilder'); // 'formbuilder / responses'
  const [submitting, setIsSubmitting] = React.useState(false);
  const [instansis, setInstansis] = React.useState([]);

  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [instansi, setInstansi] = React.useState('');

  const [rekomendasi, setRekomendasi] = React.useState('');
  const [rekomendasis, setRekomendasis] = React.useState([]);

  const getRekomendasis = async () => {
    if (_.isEmpty(user)) return;
    try {
      const idInstansi = user?.attributes?.id_instansi;

      const body = {
        id_instansi: idInstansi,
        role: user?.attributes?.role
      };
      const result = await myLayananService.getRekomendasis(body);

      const data = [];

      result.data.forEach((item) => {
        data.push({
          label: `${item?.attributes?.name} - ${item?.attributes?.createdAt} - ${item?.attributes?.status}`,
          id: item?.id,
          meta: item
        });
      });

      if (uat?.meta?.id_rekomendasi) {
        const rekomendasiValue = data.filter((item) => item.id === uat?.meta?.id_rekomendasi);
        setRekomendasi(rekomendasiValue[0]);
      }

      setRekomendasis(data);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getInstansis = async () => {
    try {
      const result = await instansiService.getAll();
      const { data } = result;

      const newInstansis = [];
      data.forEach((item) => {
        newInstansis.push({ id: item?.id_instansi, label: item?.name });
      });

      const filteredData = ['admin', 'superadmin'].includes(role)
        ? newInstansis
        : newInstansis.filter((item) => item.id === user?.attributes?.id_instansi);
      setInstansis(filteredData);
    } catch (error) {
      toast.error(error.message);
    }
  };

  React.useEffect(() => {
    getInstansis();
  }, [role]);

  const getUAT = async () => {
    try {
      if (!id) return toast.error('Maaf, gagal mendapatkan ID UAT');
      const body = {
        id,
        role: user?.attributes?.role
      };
      const result = await uatServices.getByID(body);
      setUat(result.data);
      setTitle(result.data.attributes.title);
      if (result.data.attributes.id_instansi) {
        setInstansi({
          id: result?.data?.relationships?.instansi?.id_instansi,
          label: result?.data?.relationships?.instansi?.name
        });
      }

      setDescription(result.data.attributes.description);
    } catch (error) {
      toast.error(error.message);
    }
  };

  React.useEffect(() => {
    getUAT();
  }, [toggleRefresh, selectedMenu]);

  React.useEffect(() => {
    getRekomendasis();
  }, [uat]);

  const updateUAT = async (complements, questions) => {
    try {
      let body = {};
      if (_.isEmpty(complements) && _.isEmpty(questions)) {
        if (title === 'Nama Aplikasi')
          return toast.error('Harap ubah nama aplikasi terlebih dahulu');
        if (!rekomendasi?.id) return toast.error('Harap pilih nomor rekomendasi terlebih dahulu');

        const currentRekomendasi = rekomendasis[findIndex(rekomendasis, rekomendasi?.id, 'id')];

        if (currentRekomendasi?.meta?.attributes?.status !== 'approved')
          return toast.error(
            `Maaf, permohonan rekomendasi ${currentRekomendasi?.meta?.attributes?.name} belum disetujui`
          );

        body = {
          data: {
            id: uat.id,
            attributes: {
              title,
              description,
              id_instansi: instansi.id
            },
            meta: { ...uat.meta, id_rekomendasi: rekomendasi.id }
          }
        };
      } else {
        body = {
          data: {
            id: uat.id,
            meta: { ...uat.meta, complements, questions }
          }
        };
      }

      setIsSubmitting(true);
      toast
        .promise(uatServices.updateForm(body), {
          loading: 'Menyimpan data ...',
          error: <b>Maaf, terjadi kesalahan</b>,
          success: <b>Data berhasil disimpan</b>
        })
        .then(() => {
          setToggleRefresh(!toggleRefresh);
        });
    } catch (error) {
      toast.error(error.message);
    }
    setIsSubmitting(false);
  };

  return (
    <Page title="UAT Creator | Peladen Pemda DIY">
      <Container>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2} mb={6}>
            <Icon
              cursor="pointer"
              onClick={() => navigate(-1, { replace: true })}
              icon={arrowBackFilled}
              width={32}
              height={32}
              style={{ color: theme.palette.primary.main }}
            />
            <Typography variant="h4">UAT Creator</Typography>
          </Stack>
          {_.isEmpty(uat.id) && <Typography variant="body2">ID UAT tidak ditemukan</Typography>}
          {!_.isEmpty(uat.id) && (
            <Stack direction="column" spacing={2}>
              <TextField value={title} label="Judul" onChange={(e) => setTitle(e.target.value)} />
              <TextField
                value={description}
                label="Deskripsi"
                onChange={(e) => setDescription(e.target.value)}
                multiline
              />
              <Autocomplete
                fullWidth
                disablePortal
                id="combo-box-instansi"
                options={instansis}
                value={instansi}
                onChange={(event, newValue) => {
                  setInstansi(newValue);
                }}
                renderInput={(params) => (
                  <TextField size="small" {...params} label="Pilih Instansi" />
                )}
              />
              <Autocomplete
                isRequired="true"
                fullWidth
                disablePortal
                id="combo-box-rekomendasi"
                options={rekomendasis}
                value={rekomendasi}
                onChange={(event, newValue) => {
                  setRekomendasi(newValue);
                }}
                renderInput={(params) => (
                  <TextField size="small" {...params} label="Nomor Rekomendasi" />
                )}
              />
              <RekomendasiCTA />
              <Box>
                <LoadingButton
                  onClick={() => updateUAT({}, {})}
                  variant="contained"
                  loading={submitting}
                >
                  Simpan
                </LoadingButton>
              </Box>
              {uat?.meta?.id_rekomendasi && (
                <>
                  <Divider />

                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    divider={<Divider orientation="vertical" flexItem />}
                  >
                    <Typography
                      onClick={() => setSelectedMenu('formbuilder')}
                      variant="h6"
                      sx={
                        selectedMenu === 'formbuilder'
                          ? styles.selectedMenu
                          : styles.notSelectedMenu
                      }
                    >
                      Form Builder
                    </Typography>
                    <Typography
                      onClick={() => setSelectedMenu('responses')}
                      variant="h6"
                      sx={
                        selectedMenu === 'responses' ? styles.selectedMenu : styles.notSelectedMenu
                      }
                    >
                      Responses ({uat?.meta?.answers?.length || '0'})
                    </Typography>
                  </Stack>

                  <Divider />
                  {selectedMenu === 'formbuilder' && (
                    <FormBuilder
                      user={user}
                      uat={uat}
                      toggleRefresh={toggleRefresh}
                      setToggleRefresh={setToggleRefresh}
                      updateUAT={updateUAT}
                    />
                  )}
                  {selectedMenu === 'responses' && <Responses uat={uat} />}
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Container>
    </Page>
  );
};

const Responses = ({ uat }) => {
  const data = '';
  const theme = useTheme();
  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="body2">Daftar Responses</Typography>
      {!_.isEmpty(uat?.meta?.answers) &&
        uat.meta.answers.map((item, index) => (
          <Stack direction="row" spacing={1}>
            <Typography variant="body2">{index + 1}.</Typography>
            <Typography variant="body2">{item?.attributes?.creator}</Typography>
            <Typography variant="body2">-</Typography>
            <Typography variant="body2">
              {moment.tz(item?.attributes?.createdAt, 'Asia/Jakarta').format('YYYY-MM-DD')}
            </Typography>
            <Icon
              onClick={() =>
                window.open(`${getBaseURL()}/uat/response/${uat.id}/${item.id}`, '_blank')
              }
              icon={eyeFill}
              width={24}
              height={24}
              style={{ color: theme.palette.primary.main, cursor: 'pointer' }}
            />
          </Stack>
        ))}
    </Stack>
  );
};

const FormBuilder = ({ user, uat, toggleRefresh, setToggleRefresh, updateUAT }) => {
  const { id } = useParams();
  const data = '';
  const theme = useTheme();
  const navigate = useNavigate();
  const width = useWidth(useTheme, useMediaQuery);

  const [mode, setMode] = React.useState('asking'); // asking or filling
  const [editIndex, setEditIndex] = React.useState(); // to mark edit question index
  const [complement, setComplement] = React.useState('menu'); // menu or roles
  const [complementName, setComplementName] = React.useState('');
  const [complements, setComplements] = React.useState({ menu: [], roles: [] });
  const [questions, setQuestions] = React.useState([]);
  const [form, setForm] = React.useState('');
  const [complementMenu, setComplementMenu] = React.useState();
  const [complementRole, setComplementRole] = React.useState();
  const [complementsCombo, setComplementsCombo] = React.useState({ menu: [], roles: [] });

  const updateComplements = async () => {
    setComplements(uat?.meta?.complements || { menu: [], roles: [] });
  };

  const updateQuestions = async () => {
    setQuestions(uat?.meta?.questions || []);
  };

  React.useEffect(() => {
    updateComplements();
    updateQuestions();
  }, [uat]);

  const addComplement = async () => {
    if (!complementName || !complement) return toast.error('Nama menu/role belum diisi');

    const id = uuidv4();

    const data = !_.isEmpty(complements[complement]) ? complements[complement] : [];

    // const data = {
    //   ...complements,
    //   [complement]: {
    //     ...complements[complement],
    //     id,
    //     type: complement,
    //     attributes: { name: complementName }
    //   }
    // };

    data.push({ id, type: complement, attributes: { name: complementName } });

    setComplements({ ...complements, [complement]: data });
    setComplementName('');
  };

  const deleteComplement = async (item) => {
    const index = findIndex(complements[item.type], item.id, 'id');

    if (index > -1) {
      const data = complements[item.type].filter((item, itemIndex) => itemIndex !== index);
      setComplements({ ...complements, [item.type]: data });
    }
  };

  const updateComboList = () => {
    const dataMenu = [];
    complements?.menu.forEach((item) => {
      dataMenu.push({ id: item.id, label: item.attributes.name });
    });

    const dataRole = [];
    complements?.roles.forEach((item) => {
      dataRole.push({ id: item.id, label: item.attributes.name });
    });

    setComplementsCombo({ ...complementsCombo, menu: dataMenu, roles: dataRole });
  };

  React.useEffect(() => {
    updateComboList();
  }, [updateUAT]);

  const addQuestion = () => {
    try {
      if (!form.label) return toast.error('Pertanyaan belum diisi');
      if (!form.menu) return toast.error('Menu belum dipilih');
      if (_.isEmpty(form.roles)) return toast.error('Roles belum dipilih');

      // validating roles
      let counts = 0;
      form.roles.forEach((item) => {
        if (item.checked) counts += 1;
      });

      if (counts < 1) return toast.error('Roles belum dipilih');

      if (mode === 'fillingupdate') {
        const data = [...questions];
        data[editIndex].attributes.label = form.label;
        data[editIndex].attributes.menu = form.menu;
        data[editIndex].attributes.roles = form.roles;

        setQuestions(data);
        setMode('asking');
        setForm({});
        updateUAT(complements, data);
      } else {
        const index = questions.length;
        const id = uuidv4();
        const data = [...questions];
        data.push({
          id,
          type: 'uat',
          attributes: { label: form.label, menu: form.menu, roles: form.roles, index }
        });

        setQuestions(data);
        setMode('asking');
        setForm({});
        updateUAT(complements, data);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangeCheckbox = (index, value) => {
    const roles = form.roles || [];

    if (!_.isEmpty(roles) && roles.length >= index + 1) {
      roles[index].checked = value;
      roles[index].id = complementsCombo.roles[index].id;
      roles[index].label = complementsCombo.roles[index].label;
    } else {
      roles.push({
        checked: value,
        id: complementsCombo.roles[index].id,
        label: complementsCombo.roles[index].label
      });
    }
    setForm({ ...form, roles });
  };

  const deleteQuestion = (item, index) => {
    // const questionIndex = findIndex(questions, item.id, 'id');
    const newQuestions = questions.filter((qItem, qIndex) => qItem.id !== item.id);
    updateUAT(complements, newQuestions);
    // alert(newQuestions);
  };

  const toggleEditQuestion = (item, index) => {
    setMode('fillingupdate');
    setEditIndex(index);
    setForm({
      roles: item?.attributes?.roles,
      menu: item?.attributes?.menu,
      label: item?.attributes?.label
    });
  };

  const changeQuestionPosition = (item, index, target) => {
    // target = up/down
    try {
      if (target === 'up' && index === 0) return;
      if (target === 'down' && index === questions.length - 1) return;
      const data = [...questions];
      const newQuestions = [...questions];

      const targetValue = target === 'down' ? 1 : -1;

      newQuestions[index] = data[index + targetValue];
      newQuestions[index + targetValue] = data[index];

      setQuestions(newQuestions);
      updateUAT(complements, newQuestions);
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Stack direction="column">
      <Stack
        direction={['xs', 'sm'].includes(width) ? 'column' : 'row'}
        spacing={2}
        divider={<Divider orientation="vertical" flexItem />}
      >
        <Stack direction="column" spacing={2} sx={{ flex: 8 }}>
          <Stack direction="column" spacing={2}>
            {!_.isEmpty(questions) &&
              questions.map((item, index) => (
                <Stack direction="column" spacing={2}>
                  {mode === 'fillingupdate' && editIndex === index ? (
                    <Stack direction="column" spacing={2}>
                      <TextField
                        multiline="true"
                        fullWidth
                        label="Pertanyaan"
                        placeholder="Pertanyaan"
                        value={form.label}
                        onChange={(e) => setForm({ ...form, label: e.target.value })}
                      />
                      {!_.isEmpty(complementsCombo?.menu) ? (
                        <Autocomplete
                          disablePortal
                          id="combo-box-menu"
                          options={complementsCombo.menu}
                          value={form.menu}
                          onChange={(event, newValue) => {
                            setForm({ ...form, menu: newValue });
                          }}
                          sx={{ width: 300 }}
                          renderInput={(params) => (
                            <TextField size="small" {...params} label="Menu" />
                          )}
                        />
                      ) : (
                        <Typography variant="body2">
                          Menu tidak ditemukan, silakan tambah terlebih dahulu pada menu tambah
                          menu/roles
                        </Typography>
                      )}

                      <Typography variant="body1">Roles</Typography>
                      <FormGroup>
                        {!_.isEmpty(complementsCombo?.roles) ? (
                          complementsCombo?.roles?.map((item, index) => (
                            <FormControlLabel
                              checked={(form.roles && form?.roles[index]?.checked) || false}
                              onChange={(e) => handleChangeCheckbox(index, e.target.checked)}
                              control={<Checkbox />}
                              label={item.label}
                            />
                          ))
                        ) : (
                          <Typography variant="body2">
                            Roles tidak ditemukan, silakan tambah terlebih dahulu pada menu tambah
                            menu/roles{' '}
                          </Typography>
                        )}
                      </FormGroup>
                      <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={addQuestion}>
                          {mode === 'fillingupdate' ? 'Update Pertanyaan' : 'Tambah Pertanyaan'}
                        </Button>
                        <Button
                          variant="outlined"
                          sx={{
                            borderColor: theme.palette.error.main,
                            color: theme.palette.error.main
                          }}
                          onClick={() => setMode('asking')}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <>
                      <Stack direction="row" spacing={1}>
                        <Typography variant="body1">{index + 1}.</Typography>
                        <Typography variant="body1">{item?.attributes?.label}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        {complementsCombo.menu
                          .map((itemCM) => itemCM.label)
                          .includes(item?.attributes?.menu?.label) && (
                          <Chip
                            icon={
                              <Icon
                                icon={menu2Outline}
                                width={24}
                                height={24}
                                style={{ color: theme.palette.primary.main }}
                              />
                            }
                            variant="outlined"
                            label={item?.attributes?.menu?.label}
                          />
                        )}

                        {!_.isEmpty(item?.attributes?.roles) &&
                          item?.attributes?.roles.map(
                            (itemRole) =>
                              itemRole.label &&
                              itemRole.checked &&
                              complementsCombo.roles
                                .map((itemCM) => itemCM.label)
                                .includes(itemRole.label) && (
                                <Chip
                                  icon={
                                    <Icon
                                      icon={personOutline}
                                      width={24}
                                      height={24}
                                      style={{ color: theme.palette.info.main }}
                                    />
                                  }
                                  variant="outlined"
                                  label={itemRole.label}
                                />
                              )
                          )}
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <hr
                          style={{
                            width: 100,
                            height: 1,
                            borderColor: theme.palette.grey[100],
                            opacity: 0.5,
                            flex: 1
                          }}
                        />
                        <Icon
                          cursor="pointer"
                          onClick={() => deleteQuestion(item, index)}
                          icon={trash2Outline}
                          width={24}
                          height={24}
                          style={{ color: theme.palette.error.main }}
                        />
                        <Icon
                          cursor="pointer"
                          onClick={() => toggleEditQuestion(item, index)}
                          icon={edit2Outline}
                          width={24}
                          height={24}
                          style={{ color: theme.palette.primary.main }}
                        />
                        <Icon
                          cursor="pointer"
                          onClick={() => changeQuestionPosition(item, index, 'up')}
                          icon={arrowCircleUpOutline}
                          width={24}
                          height={24}
                          style={{
                            color: index > 0 ? theme.palette.info.main : theme.palette.grey[500]
                          }}
                        />
                        <Icon
                          cursor="pointer"
                          onClick={() => changeQuestionPosition(item, index, 'down')}
                          icon={arrowCircleDownOutline}
                          width={24}
                          height={24}
                          style={{
                            color:
                              index !== questions.length - 1
                                ? theme.palette.info.main
                                : theme.palette.grey[500]
                          }}
                        />
                      </Stack>
                    </>
                  )}
                </Stack>
              ))}
          </Stack>

          {mode === 'asking' && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>Tambah Pertanyaan</Typography>
              <Icon
                cursor="pointer"
                onClick={() => setMode('filling')}
                icon={plusCircleFill}
                width={32}
                height={32}
                style={{ color: theme.palette.primary.main }}
              />
            </Stack>
          )}

          {mode === 'asking' && (
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                onClick={() => window.open(`${getBaseURL()}/uat/preview/${id}`, '_blank')}
                startIcon={<Icon icon={eyeFill} style={{ color: '#FFFFFF' }} />}
                variant="contained"
              >
                Preview
              </Button>
              <Button
                startIcon={<Icon icon={shareFill} style={{ color: '#FFFFFF' }} />}
                variant="contained"
                onClick={() => {
                  navigator.clipboard.writeText(`${getBaseURL()}/uat/form/${id}`);
                  toast.success('Link telah di-copy');
                }}
              >
                Bagikan Link
              </Button>
            </Stack>
          )}

          {mode === 'filling' && (
            <Stack direction="column" spacing={2}>
              <TextField
                multiline="true"
                fullWidth
                label="Pertanyaan"
                placeholder="Pertanyaan"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
              />
              {!_.isEmpty(complementsCombo?.menu) ? (
                <Autocomplete
                  disablePortal
                  id="combo-box-menu"
                  options={complementsCombo.menu}
                  value={form.menu}
                  onChange={(event, newValue) => {
                    setForm({ ...form, menu: newValue });
                  }}
                  sx={{ width: 300 }}
                  renderInput={(params) => <TextField size="small" {...params} label="Menu" />}
                />
              ) : (
                <>
                  <Typography variant="body1">Menu</Typography>
                  <Typography variant="body2">
                    Menu tidak ditemukan, silakan tambah terlebih dahulu pada menu tambah menu/roles
                  </Typography>
                </>
              )}

              <Typography variant="body1">Roles</Typography>
              <FormGroup>
                {!_.isEmpty(complementsCombo?.roles) ? (
                  complementsCombo?.roles?.map((item, index) => (
                    <FormControlLabel
                      checked={(form.roles && form?.roles[index]?.checked) || false}
                      onChange={(e) => handleChangeCheckbox(index, e.target.checked)}
                      control={<Checkbox />}
                      label={item.label}
                    />
                  ))
                ) : (
                  <Typography variant="body2">
                    Roles tidak ditemukan, silakan tambah terlebih dahulu pada menu tambah
                    menu/roles
                  </Typography>
                )}
              </FormGroup>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={addQuestion}>
                  {mode === 'fillingupdate' ? 'Update Pertanyaan' : 'Tambah Pertanyaan'}
                </Button>
                <Button
                  variant="outlined"
                  sx={{ borderColor: theme.palette.error.main, color: theme.palette.error.main }}
                  onClick={() => setMode('asking')}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
        <Stack
          direction="column"
          sx={{ flex: 4 }}
          spacing={2}
          divider={<Divider orientation="horizontal" flexItem />}
        >
          <Stack direction="column" spacing={1}>
            <FormControl>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormLabel>Tambah Menu/Roles</FormLabel>
                <Tooltip
                  title="Data ini digunakan untuk memudahkan proses pembuatan pertanyaan UAT"
                  arrow
                >
                  <Icon color={theme.palette.grey[500]} icon={questionMarkCircleFill} />
                </Tooltip>
              </Stack>

              <RadioGroup
                sx={{ padding: 1 }}
                aria-labelledby="Tambah menu/role"
                value={complement}
                name="complement"
                onChange={(event) => {
                  setComplement(event.target.value);
                }}
              >
                <FormControlLabel value="menu" control={<Radio />} label="Menu" />
                <FormControlLabel value="roles" control={<Radio />} label="Role" />
              </RadioGroup>

              <TextField
                value={complementName}
                onChange={(e) => setComplementName(e.target.value)}
                label="Nama"
                placeholder="(contoh: menu dashboard, role admin, role pengguna, etc.)"
              />
            </FormControl>

            <Button onClick={addComplement} variant="outlined">
              Tambah {complement}
            </Button>
          </Stack>
          <>
            <Typography variant="body1">Daftar menu</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {_.isEmpty(complements?.menu) && (
                <Typography variant="body2">Belum ada menu</Typography>
              )}
              {!_.isEmpty(complements?.menu) &&
                complements.menu.map((item) => (
                  <Box sx={{ padding: 0.5 }} key={item.id}>
                    <Chip
                      label={item?.attributes?.name}
                      variant="outlined"
                      onDelete={() => deleteComplement(item)}
                    />
                  </Box>
                ))}
            </Stack>
          </>
          <>
            <Typography variant="body1">Daftar roles</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {_.isEmpty(complements?.roles) && (
                <Typography variant="body2">Belum ada roles</Typography>
              )}
              {!_.isEmpty(complements?.roles) &&
                complements.roles.map((item) => (
                  <Box sx={{ padding: 0.5 }} key={item.id}>
                    <Chip
                      label={item?.attributes?.name}
                      variant="outlined"
                      onDelete={() => deleteComplement(item)}
                    />
                  </Box>
                ))}
            </Stack>
          </>
          <Button variant="outlined" onClick={() => updateUAT(complements, questions)}>
            Simpan
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

FormBuilder.propTypes = {
  user: PropTypes.object.isRequired,
  uat: PropTypes.any.isRequired,
  toggleRefresh: PropTypes.func.isRequired
};

UATCreator.propTypes = {
  user: PropTypes.object.isRequired
};

export default UATCreator;
